import React, { useState, useEffect } from 'react';
import { locationService } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './Locations.css';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    description: '',
    park_timings_weekdays: '11 AM to 6 PM',
    park_timings_weekends: '11 AM to 7 PM',
    water_timings_weekdays: '12 PM to 5:30 PM',
    water_timings_weekends: '12 PM to 6 PM',
    is_active: true,
    is_new: false,
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAll();
      const data = response.data;
      setLocations(Array.isArray(data) ? data : (data?.results ?? []));
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await locationService.update(editingLocation.id, formData);
      } else {
        await locationService.create(formData);
      }
      setShowModal(false);
      setEditingLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Error saving location');
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      city: location.city,
      description: location.description || '',
      park_timings_weekdays: location.park_timings_weekdays,
      park_timings_weekends: location.park_timings_weekends,
      water_timings_weekdays: location.water_timings_weekdays,
      water_timings_weekends: location.water_timings_weekends,
      is_active: location.is_active,
      is_new: location.is_new,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await locationService.delete(id);
        fetchLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
        alert('Error deleting location');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      city: '',
      description: '',
      park_timings_weekdays: '11 AM to 6 PM',
      park_timings_weekends: '11 AM to 7 PM',
      water_timings_weekdays: '12 PM to 5:30 PM',
      water_timings_weekends: '12 PM to 6 PM',
      is_active: true,
      is_new: false,
    });
  };

  if (loading) {
    return <div className="loading">Loading locations...</div>;
  }

  return (
    <div className="locations-page">
      <div className="page-header">
        <h1>Locations Management</h1>
        <button className="btn-primary" onClick={() => { setShowModal(true); setEditingLocation(null); resetForm(); }}>
          <FiPlus />
          Add Location
        </button>
      </div>

      <div className="locations-grid">
        {(Array.isArray(locations) ? locations : []).map((location) => (
          <div key={location.id} className="location-card">
            {location.image && (
              <img src={location.image} alt={location.name} className="location-image" />
            )}
            <div className="location-info">
              <h3>{location.name}</h3>
              <p>{location.city}</p>
              <div className="location-badges">
                {location.is_new && <span className="badge new">NEW</span>}
                {location.is_active ? (
                  <span className="badge active">Active</span>
                ) : (
                  <span className="badge inactive">Inactive</span>
                )}
              </div>
            </div>
            <div className="location-actions">
              <button className="btn-edit" onClick={() => handleEdit(location)}>
                <FiEdit2 />
              </button>
              <button className="btn-delete" onClick={() => handleDelete(location.id)}>
                <FiTrash2 />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingLocation(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingLocation ? 'Edit Location' : 'Add Location'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Park Timings (Weekdays)</label>
                  <input
                    type="text"
                    value={formData.park_timings_weekdays}
                    onChange={(e) => setFormData({ ...formData, park_timings_weekdays: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Park Timings (Weekends)</label>
                  <input
                    type="text"
                    value={formData.park_timings_weekends}
                    onChange={(e) => setFormData({ ...formData, park_timings_weekends: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Water Timings (Weekdays)</label>
                  <input
                    type="text"
                    value={formData.water_timings_weekdays}
                    onChange={(e) => setFormData({ ...formData, water_timings_weekdays: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Water Timings (Weekends)</label>
                  <input
                    type="text"
                    value={formData.water_timings_weekends}
                    onChange={(e) => setFormData({ ...formData, water_timings_weekends: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_new}
                    onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
                  />
                  Mark as New
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingLocation(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;
